import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Save, Trash2 } from 'lucide-react';
import {
  useScreenerPresets,
  useCreateScreenerPreset,
  useDeleteScreenerPreset,
  useUpdateScreenerPreset,
} from '../api/hooks.js';
import { ApiError, type ScreenerPresetRow } from '../api/client.js';

// SaxoTrader-style saved filter picker. Sits at the top-left of the
// Screener toolbar. Behavior:
//   - Dropdown lists every preset for the current user
//   - "Save current as preset…" opens an inline name field, then POST
//   - "Save changes" overwrites the currently-loaded preset
//   - Trash icon per row deletes the preset
//
// Filter shape is opaque to this component — the parent passes
// `currentFilter` (whatever JSON it wants) and `onApply(filterJson)`
// to receive a clicked preset back. Loaded-preset id is tracked here
// so "Save changes" knows what to update.

export interface ScreenerPresetPickerProps {
  /** The Screener's current filter snapshot to save. */
  currentFilter: Record<string, unknown>;
  /** Called when the user picks a preset. */
  onApply: (filterJson: Record<string, unknown>) => void;
}

export function ScreenerPresetPicker({ currentFilter, onApply }: ScreenerPresetPickerProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useScreenerPresets();
  const createMut = useCreateScreenerPreset();
  const updateMut = useUpdateScreenerPreset();
  const deleteMut = useDeleteScreenerPreset();

  const [open, setOpen] = useState(false);
  const [naming, setNaming] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    function onClick(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setNaming(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const presets = data?.data ?? [];
  const loaded = presets.find((p) => p.id === loadedId);
  const label = loaded ? loaded.name : t('screener.presets.pick');

  function apply(p: ScreenerPresetRow) {
    onApply(p.filterJson);
    setLoadedId(p.id);
    setOpen(false);
  }
  function startNaming() {
    setDraftName('');
    setErrorMessage(null);
    setNaming(true);
  }
  function commitNew() {
    const name = draftName.trim();
    if (!name) return;
    setErrorMessage(null);
    createMut.mutate(
      { name, filterJson: currentFilter },
      {
        onSuccess: (res) => {
          setLoadedId(res.data.id);
          setNaming(false);
          setOpen(false);
        },
        onError: (err) => {
          setErrorMessage(describeError(err, t));
        },
      },
    );
  }
  function overwriteLoaded() {
    if (!loadedId) return;
    setErrorMessage(null);
    updateMut.mutate(
      { id: loadedId, filterJson: currentFilter },
      {
        onSuccess: () => setOpen(false),
        onError: (err) => setErrorMessage(describeError(err, t)),
      },
    );
  }
  function removePreset(id: string) {
    setErrorMessage(null);
    deleteMut.mutate(id, {
      onSuccess: () => {
        if (loadedId === id) setLoadedId(null);
      },
      onError: (err) => setErrorMessage(describeError(err, t)),
    });
  }

  return (
    <div ref={rootRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '2px var(--s-2)',
          background: 'var(--menu-surface)',
          border: '1px solid var(--hairline)',
          borderRadius: 2,
          fontSize: 'var(--t-body)',
          color: 'var(--text-primary)',
          cursor: 'pointer',
          fontWeight: loaded ? 600 : 500,
          maxWidth: 200,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={loaded ? loaded.name : t('screener.presets.pickTooltip')}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
        <ChevronDown size={12} />
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: 4,
            minWidth: 220,
            maxWidth: 320,
            background: 'var(--menu-surface)',
            border: '1px solid var(--hairline)',
            borderRadius: 2,
            boxShadow: '0 6px 24px rgba(10,14,26,0.14), 0 2px 6px rgba(10,14,26,0.08)',
            zIndex: 9000,
            padding: 4,
            fontSize: 'var(--t-body)',
          }}
        >
          {isLoading && <Hint>{t('screener.presets.loading')}</Hint>}
          {!isLoading && presets.length === 0 && <Hint>{t('screener.presets.empty')}</Hint>}
          {presets.map((p) => (
            <div
              key={p.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                alignItems: 'center',
                padding: '4px 6px',
                cursor: 'pointer',
                background: p.id === loadedId ? 'var(--color-accent-100)' : 'transparent',
                borderRadius: 2,
              }}
              onMouseOver={(e) => {
                if (p.id !== loadedId) e.currentTarget.style.background = 'var(--bg-sunken)';
              }}
              onMouseOut={(e) => {
                if (p.id !== loadedId) e.currentTarget.style.background = 'transparent';
              }}
            >
              <button
                type="button"
                onClick={() => apply(p)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  textAlign: 'left',
                  font: 'inherit',
                  color: 'inherit',
                  cursor: 'pointer',
                  padding: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {p.name}
              </button>
              <button
                type="button"
                aria-label={t('screener.presets.delete')}
                title={t('screener.presets.delete')}
                onClick={(e) => {
                  e.stopPropagation();
                  removePreset(p.id);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--text-tertiary)',
                  padding: 2,
                }}
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
          <div
            style={{
              borderTop: '1px solid var(--hairline)',
              marginTop: 4,
              paddingTop: 4,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {naming ? (
              <div style={{ display: 'flex', gap: 4, padding: '2px 4px' }}>
                <input
                  autoFocus
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  placeholder={t('screener.presets.namePlaceholder')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitNew();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      setNaming(false);
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '2px 4px',
                    border: '1px solid var(--accent)',
                    borderRadius: 2,
                    background: 'var(--surface)',
                    color: 'var(--text-primary)',
                    fontSize: 'var(--t-body)',
                  }}
                />
                <button
                  type="button"
                  onClick={commitNew}
                  disabled={!draftName.trim() || createMut.isPending}
                  style={menuBtn()}
                >
                  {t('screener.presets.save')}
                </button>
              </div>
            ) : (
              <button type="button" onClick={startNaming} style={menuBtn()}>
                <Save size={12} style={{ marginRight: 6 }} />
                {t('screener.presets.saveAsNew')}
              </button>
            )}
            {loaded && !naming && (
              <button type="button" onClick={overwriteLoaded} style={menuBtn()}>
                {t('screener.presets.overwrite', { name: loaded.name })}
              </button>
            )}
          </div>
          {errorMessage && (
            <div
              role="alert"
              style={{
                marginTop: 6,
                padding: '4px 8px',
                background: 'color-mix(in srgb, var(--color-negative-500) 8%, transparent)',
                color: 'var(--color-negative-500)',
                fontSize: 'var(--t-meta)',
                border: '1px solid var(--color-negative-500)',
                borderRadius: 2,
              }}
            >
              {errorMessage}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function describeError(
  err: unknown,
  t: (k: string, opts?: Record<string, unknown>) => string,
): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return t('screener.presets.errorUnauth');
    if (err.status === 409) return t('screener.presets.errorConflict');
    return t('screener.presets.errorGeneric', { status: err.status });
  }
  return t('screener.presets.errorOffline');
}

function menuBtn(): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 6px',
    background: 'transparent',
    border: 'none',
    font: 'inherit',
    color: 'var(--text-primary)',
    cursor: 'pointer',
    borderRadius: 2,
    textAlign: 'left',
    width: '100%',
  };
}

function Hint({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: '4px 6px',
        color: 'var(--text-tertiary)',
        fontSize: 'var(--t-meta)',
      }}
    >
      {children}
    </div>
  );
}
